import type {
  Country,
  DateTime,
  MarketCountryCode,
  MonetaryAmount,
  Url,
} from '@backmarket/http-api'

import type { MessageKind } from '../care-folders/types/common'

export type AttachmentToApi = {
  /**
   * Unique identifier for the uploaded file
   */
  handle: string
  /**
   * Name of the storage bucket where the file is stored
   */
  bucketName?: string
  /**
   * Path to the file within the bucket
   */
  filePath: string
}

export interface OrderlineMessageBody {
  message: string
  attachments: AttachmentToApi[]
}

type PendingProblem = {
  id: string
  type: 'DAMAGED_PACKAGE' | 'OOW_ITEM' | 'ITEM_NOT_RECEIVED'
  status: 'PENDING_AGENT'
}

export const Actions = {
  refund: 'REFUND',
  postMessage: 'POST_MESSAGE',
  problem: 'PROBLEM',
  createManualReturnOfProductReturn: 'CREATE_MANUAL_RETURN_OF_PRODUCT_RETURN',
} as const

export type PartialRefundReason =
  | 'ACCESSORY_PROBLEM'
  | 'RETURN_LABEL_FEES'
  | 'THIRD_PARTY_REPAIR'
  | 'APPEARANCE_ISSUE'
  | 'PERFORMANCE_ISSUE'
  | 'OTHER'

export type RefundAttributes = {
  isAllowedPartial: boolean
  refundPrice: MonetaryAmount
  partialRefundReasons: PartialRefundReason[]
  isCustomerBlocked: boolean
  hasActiveInsurancePolicies: boolean
  orderlineQuantity: number
}

export type MessageAttributes = {
  replyToAgent: boolean
  reportProblem: boolean
}

export type ProblemAttributes = {
  types: [
    | 'ACCOUNT_LOCKED'
    | 'CODE_LOCKED'
    | 'DAMAGED_PACKAGE'
    | 'ITEM_NOT_RECEIVED'
    | 'MISSING_PART'
    | 'OOW_ITEM'
    | 'PROBLEM_NOT_IDENTIFIED'
    | 'WRONG_ITEM'
    | 'OTHER',
  ]
}

export type AttributesType =
  | RefundAttributes
  | MessageAttributes
  | ProblemAttributes
  | null

export type ActionType = (typeof Actions)[keyof typeof Actions]

export interface ActionsResponse {
  pendingProblems: PendingProblem[]
  actions: {
    action: ActionType
    isDisabled: boolean
    attributes: AttributesType
  }[]
}

export type Attachment = {
  /**
   * Unique identifier for the uploaded file
   */
  id?: string
  /**
   * Name of the attachment, including the extension.
   */
  name: string
  /**
   * URL of the attachment.
   * The URL is signed and includes an 'expires' parameter containing a Unix timestamp (in seconds) indicating when the URL will expire.
   */
  url: Url
  /**
   * Content-type coming from the provider, e.g.: image/png, application/pdf, etc.
   */
  contentType: string | null
  /**
   * Date when the attachment was created (the same date as the message to which it was attached).
   */
  creationDate?: DateTime
}

export type AttachmentsByDate = {
  date: DateTime
  files: Attachment[]
}

export type CustomerIssue = {
  id: string
  label: string
}

export type LabelReturn = {
  shipmentLabelUrl: Url
  qrCodeUrl: Url | null
}

export type ResolutionType =
  | 'FACTORY_REPAIR'
  | 'SHOP_REPAIR'
  | 'REPAIR_REPLACE'
  | 'SELLER_REPLACE'
  | 'REPLACE'
  | 'REMOTE_ASSISTANCE'
  | 'REFUND'
  | 'SELLER_REPAIR'
  | 'LIVE_CHAT'
  | 'STRAIGHT_REFUND'
  | 'EXPRESS_REPLACE'
  | 'INTERCOM_ASYNC_CHAT'
  | 'INTERCOM_LIVE_CHAT'

export type Message = {
  messageId: number
  content: string
  creationDate: DateTime
  sender: {
    senderId: number
    displayName: string
  }
  kind: MessageKind
  attachments: Attachment[]
  isInformative: boolean
}
export type DeclaredWarrantyState =
  | 'IN_WARRANTY'
  | 'WATER_OR_DROPPED'
  | 'THIRD_PARTY_REPAIR'

export interface Conversation {
  requestId: string
  creationDate: string
  customerIssues: CustomerIssue[]
  declaredWarrantyState: DeclaredWarrantyState | null
  labelReturn: LabelReturn | null
  resolutionType?: ResolutionType | null
  messages: Message[]
}

export type ConversationResponse = Conversation[]

export type RequestStatusType =
  | 'TODO'
  | 'UPCOMING'
  | 'NO_ACTION_NEEDED'
  | 'COMPLETED'
  | 'LATE'

export const TASK = [
  'SEND_MESSAGE',
  'UPLOAD_RECEIPT',
  'REPAIR_REPLACE',
  'REPAIR',
  'REPLACE',
  'REFUND',
  'RETURN_TO_CUSTOMER',
  'REPAIR_REPLACE_REFUND',
] as const

export type TaskType = (typeof TASK)[number]

export type TaskStatusType = 'OPEN' | 'UPCOMING'

export type OrderlineOpenTask = {
  id: string
  type: TaskType
  status: TaskStatusType
  deadline: DateTime
}

export type OrderlineRequest = {
  id: string
  status: RequestStatusType
  resolutionId: string | null
  openTasks: OrderlineOpenTask[]
}

export type OrderlineRequestsResponse = OrderlineRequest[]

export type Problem = {
  id: string
  type: 'DAMAGED_PACKAGE' | 'OOW_ITEM' | 'ITEM_NOT_RECEIVED'
  status: 'PENDING_AGENT'
}

export type ProblemType =
  | 'ACCOUNT_LOCKED'
  | 'CODE_LOCKED'
  | 'DAMAGED_PACKAGE'
  | 'ITEM_NOT_RECEIVED'
  | 'MISSING_PART'
  | 'OOW_ITEM'
  | 'PROBLEM_NOT_IDENTIFIED'
  | 'WRONG_ITEM'
  | 'OTHER'

export type Address = {
  publicId: string
  firstName: string | null
  lastName: string | null
  street: string
  street2: string
  postalCode: string
  city: string
  country: Country
  countryDialInCode: string | null
  phone: string
  stateOrProvince: string | null
  companyName: string | null
  firstNamePronunciation: string | null
  lastNamePronunciation: string | null
}

export type ProductReturn = {
  creationDate: string
  shipmentPublicId: string
  kind:
    | 'CUSTOMER_TO_SELLER'
    | 'SELLER_TO_CUSTOMER'
    | 'CUSTOMER_TO_REPAIR_FACTORY'
    | 'REPAIR_FACTORY_TO_CUSTOMER'
  shipment: {
    trackingNumber: string
    dispatchAddress: Address | null
    isBackship: boolean
  } | null
  datePickedUp: string | null
  dateDelivered: string | null
}

export type Issue = {
  name: string
  category: string
}

export type Contact = {
  id: string
  helpRequestId: string
  issues: Array<Issue>
  requestedResolution: string | null
  extraData?: {
    warrantyExclusion: boolean
  }
  createdAt: string
}

export type HelpRequest = {
  id: string
  businessUnit: 'SALES' | 'TRADE_IN'
  market: MarketCountryCode
  state: string
  stateCategory:
    | 'SUBMITTED'
    | 'IN_PROGRESS'
    | 'WAITING_ON_CUSTOMER'
    | 'RESOLVED'
  requester: {
    type: 'CUSTOMER' | 'SELLER'
    id: string
  }
  impactedEntity: {
    type: 'SALES_ORDERLINE' | 'TRADE_IN_ORDER'
    id: string
  }
  createdAt: string
  resolvedAt?: string | null
  contacts?: Array<Contact>
  conversation?: {
    id: string
    helpRequestId: string
    intercomType: 'TICKET' | 'CONVERSATION'
    intercomId: number
    createdAt: string
    intercomUrl: string
  } | null
}
